import { sceneVisualManifest } from "@/assets/v2/manifest";
import type { TicketData } from "@/types/journey";

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let line = "";
  for (const character of Array.from(text)) {
    const next = line + character;
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line);
      line = character;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  return lines.length ? lines : [""];
}

export const ticketLettering = {
  mobile: { width: 941, height: 1672, x: 205, y: 1054, lineWidth: 478, textHeight: 108, angle: -4, skew: 0, maxFont: 34 },
  desktop: { width: 1672, height: 941, x: 685, y: 530, lineWidth: 552, textHeight: 70, angle: 0, skew: 0, maxFont: 28 },
} as const;
export const ticketFontFamily = "'STKaiti', 'KaiTi', 'Kaiti SC', 'BiauKai', 'Noto Serif SC', serif";
export type TicketProfile = keyof typeof ticketLettering;
export const ticketInk = { color: "#493e30", opacity: .9, weight: 400 } as const;
export const ticketExportDimensions = { mobile: { width: 1080, height: 1440 }, desktop: { width: 1672, height: 941 } } as const;
export const ticketSessionLettering = {
  mobile: { offsetY: 130, fontSize: 17 },
  desktop: { offsetY: 85, fontSize: 14 },
} as const;
/** Local paper axes, calibrated to the printed answer frame rather than screen edges.
 * Rotation follows its baseline; shear follows the side of the paper in perspective.
 * Both SVG and canvas consume this exact matrix, including the date/code line. */
export function ticketPlaneMatrix(profile: TicketProfile): [number, number, number, number, number, number] {
  const area = ticketLettering[profile];
  const angle = area.angle * Math.PI / 180;
  const c = Math.cos(angle), s = Math.sin(angle);
  return [c, s, c * area.skew - s, s * area.skew + c, area.x, area.y];
}
export function ticketSessionLabel(ticket: Pick<TicketData, "issuedDate" | "ticketCode">): string {
  return `${ticket.issuedDate}  ·  ${ticket.ticketCode}`;
}

/** Same original-image coordinates for the live inscription and exported PNG. */
export function layoutTicketInscription(ctx: CanvasRenderingContext2D, text: string, profile: keyof typeof ticketLettering) {
  const area = ticketLettering[profile];
  for (let fontSize: number = area.maxFont; fontSize >= 10; fontSize--) {
    ctx.font = `${ticketInk.weight} ${fontSize}px ${ticketFontFamily}`;
    const lines = wrapText(ctx, text, area.lineWidth);
    const lineHeight = fontSize * 1.48;
    if (lines.length * lineHeight <= area.textHeight || fontSize === 10) {
      return { ...area, fontSize, lineHeight, lines, offsetY: Math.max(0, (area.textHeight - lines.length * lineHeight) / 2) };
    }
  }
  throw new Error("TICKET_TEXT_LAYOUT_FAILED");
}

export function fitActionText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): { fontSize: number; lines: string[] } {
  for (const fontSize of [54, 50, 46, 42, 38, 34, 30]) {
    ctx.font = "700 " + fontSize + "px 'Noto Serif SC', 'Songti SC', serif";
    const lines = wrapText(ctx, text, maxWidth);
    if (lines.length <= 4) return { fontSize, lines };
  }
  ctx.font = "700 28px 'Noto Serif SC', 'Songti SC', serif";
  return { fontSize: 28, lines: wrapText(ctx, text, maxWidth) };
}

function loadImage(source: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("TICKET_BASE_UNAVAILABLE"));
    image.src = source;
  });
}

export async function renderTicketPngBlob(ticket: TicketData, profile: TicketProfile = "mobile"): Promise<Blob> {
  await document.fonts.ready;
  const canvas = document.createElement("canvas");
  canvas.width = ticketExportDimensions[profile].width;
  canvas.height = ticketExportDimensions[profile].height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("CANVAS_UNAVAILABLE");

  const image = await loadImage(sceneVisualManifest.ticket[profile]);
  const scale = Math.max(canvas.width / image.naturalWidth, canvas.height / image.naturalHeight);
  const width = image.naturalWidth * scale;
  const height = image.naturalHeight * scale;
  const x = (canvas.width - width) / 2;
  const y = (canvas.height - height) / 2;
  ctx.drawImage(image, x, y, width, height);

  ctx.save();
  ctx.globalCompositeOperation = "multiply";
  ctx.globalAlpha = ticketInk.opacity;
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  const layout = layoutTicketInscription(ctx, ticket.action, profile);
  ctx.transform(...ticketPlaneMatrix(profile));
  ctx.fillStyle = ticketInk.color;
  ctx.textBaseline = "alphabetic";
  ctx.font = `${ticketInk.weight} ${layout.fontSize}px ${ticketFontFamily}`;
  layout.lines.forEach((line, index) => {
    ctx.fillText(line, 0, layout.offsetY + layout.fontSize + index * layout.lineHeight);
  });

  ctx.restore();

  const stamp = ticketSessionLettering[profile];
  ctx.save();
  ctx.globalCompositeOperation = "multiply";
  ctx.globalAlpha = ticketInk.opacity;
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.transform(...ticketPlaneMatrix(profile));
  ctx.fillStyle = ticketInk.color;
  ctx.font = `${ticketInk.weight} ${stamp.fontSize}px ${ticketFontFamily}`;
  ctx.fillText(ticketSessionLabel(ticket), 0, stamp.offsetY);
  ctx.restore();

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => value ? resolve(value) : reject(new Error("PNG_EXPORT_FAILED")), "image/png");
  });
}

export async function downloadTicketPng(ticket: TicketData, filename = "red-boat-ticket.png", profile: TicketProfile = "mobile"): Promise<void> {
  const blob = await renderTicketPngBlob(ticket, profile);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
